import type { SalesResolveOrderItem } from "../types/sales";
import { orderLineKey } from "utils/orderResolveLinePricing";
import { isOrderLineObjectId, salesLineIdentityKey } from "utils/orderLineObjectId";
import { round2 } from "utils/fixedAmountDiscountAllocation";
import { readResolveTaxable, resolveCatalogProductLineTaxable } from "../utils/readResolveTaxable";

export type BuildSalesResolveCatalogRow = {
  _id?: string;
  line_item_id?: string;
  productId: string;
  variantId: string;
  quantity?: number;
  unitPrice?: number;
  sku?: string;
  is_taxable?: boolean;
  is_production?: boolean;
  attributesList?: Array<{ attributes?: Record<string, string> }>;
  images?: unknown[];
  customizations?: unknown;
  design_id?: string;
  design_name?: string;
  preview_url?: string;
  product_type?: string;
};

export type BuildSalesResolveCustomRow = {
  _id?: string;
  line_item_id?: string;
  product_id: string;
  variant_id: string;
  quantity?: number;
  Price?: number;
  item_name?: string;
  is_taxable?: boolean;
  item_tax_info?: { taxable?: boolean };
  custom_information?: string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  weight?: number | string;
};

export type BuildSalesResolveOrderItemsInput = {
  selectedProducts?: BuildSalesResolveCatalogRow[] | null;
  customproducts?: BuildSalesResolveCustomRow[] | null;
  productObjs?: Array<{
    product_id: string;
    product?: {
      purchase_price?: number;
      product_type?: string;
      variants?: Array<{
        variant_id: string;
        variant_name?: string;
        sku?: string;
        inventory?: unknown[];
      }>;
    };
  }> | null;
  manualUnitPriceMap: Record<string, number>;
  /** Case 1: catalog base. Case 2: user-edited unit price. */
  getUnitRetailPriceForPayload: (
    productId: string,
    variantId: string,
    originalUnitPrice: number,
    lineId?: string
  ) => number;
  /** Case 1: effective unit after volume/qty discount. Case 2: same as retail (manual). */
  getChangedUnitPriceForPayload: (
    productId: string,
    variantId: string,
    originalUnitPrice: number,
    lineId?: string
  ) => number;
  inventorySource?: string;
};

function pickStorageLocation(variant: { inventory?: unknown[] } | undefined): unknown {
  const locations = Array.isArray(variant?.inventory) ? variant!.inventory! : [];
  if (locations.length === 0) return null;
  if (locations.length === 1) {
    const w = locations[0] as Record<string, unknown>;
    return {
      warehouse_id: w.warehouse_id,
      warehouse: w.warehouse_name,
      zone_id: w.zone_id,
      zone: w.zone_name,
      rack_id: w.rack_id,
      rack: w.rack_name,
      bin_id: w.bin_id,
      bin: w.bin_name,
    };
  }
  const available =
    (locations as Array<Record<string, unknown>>).find((w) => Number(w.available) > 0) ||
    locations[locations.length - 1];
  const w = available as Record<string, unknown>;
  return {
    warehouse_id: w.warehouse_id,
    warehouse: w.warehouse_name,
    zone_id: w.zone_id,
    zone: w.zone_name,
    rack_id: w.rack_id,
    rack: w.rack_name,
    bin_id: w.bin_id,
    bin: w.bin_name,
  };
}

/**
 * Build catalog + custom line items for sales resolve.
 * Coupon / assignment discounts are computed server-side — no line discount fields sent.
 */
export function buildSalesResolveOrderItems(
  input: BuildSalesResolveOrderItemsInput
): SalesResolveOrderItem[] {
  const {
    selectedProducts,
    customproducts,
    productObjs,
    manualUnitPriceMap,
    getUnitRetailPriceForPayload,
    getChangedUnitPriceForPayload,
    inventorySource = "signage",
  } = input;

  const catalogItems: SalesResolveOrderItem[] = (selectedProducts || []).map((p) => {
    const productObj = productObjs?.find((o) => o.product_id === p.productId);
    const variant = productObj?.product?.variants?.find((v) => v.variant_id === p.variantId);
    const lineId = salesLineIdentityKey(p);
    const key = isOrderLineObjectId(lineId) ? lineId : orderLineKey(p.productId, p.variantId);
    const qty = Math.max(1, Number(p.quantity ?? 1));
    const originalUnit = Number(p.unitPrice ?? 0);
    const isManual = manualUnitPriceMap[key] !== undefined;
    const unitRetail = round2(
      isManual
        ? Number(manualUnitPriceMap[key])
        : getUnitRetailPriceForPayload(p.productId, p.variantId, originalUnit, lineId)
    );
    const changedUnit = round2(
      isManual
        ? Number(manualUnitPriceMap[key])
        : getChangedUnitPriceForPayload(p.productId, p.variantId, originalUnit, lineId)
    );
    const purchasePrice = Number(productObj?.product?.purchase_price || 0);
    const hasStudio = Boolean(p.design_id && p.design_name && p.preview_url);
    const payloadImages = hasStudio
      ? [{ url: String(p.preview_url) }]
      : Array.isArray(p.images)
        ? p.images
        : [];
    const is_taxable =
      typeof p.is_taxable === "boolean"
        ? p.is_taxable
        : resolveCatalogProductLineTaxable({
            catalogProduct: productObj?.product as Record<string, unknown> | undefined,
            row: p as Record<string, unknown>,
          });

    return {
      ...(isOrderLineObjectId(lineId) ? { _id: lineId } : {}),
      product_id: p.productId,
      variant_id: p.variantId,
      product_name: String(variant?.variant_name ?? ""),
      sku: String(variant?.sku ?? p.sku ?? ""),
      quantity: qty,
      unit_purchase_price: purchasePrice,
      unit_retail_price: unitRetail,
      changed_unit_price: changedUnit,
      is_price_overridden: isManual,
      inventory_source: inventorySource,
      is_taxable,
      is_production: !!p.is_production,
      storage_location: variant ? pickStorageLocation(variant) : null,
      attributes: p.attributesList?.[0]?.attributes || {},
      images: payloadImages,
      customizations: p.customizations,
      ...(p.design_id
        ? {
            design_id: p.design_id,
            design_name: p.design_name || "",
            preview_url: p.preview_url || "",
          }
        : {}),
      product_type:
        String(p.product_type ?? productObj?.product?.product_type ?? "simple").trim() || "simple",
    };
  });

  const customItems: SalesResolveOrderItem[] = (customproducts || []).map((cp) => {
    const qty = Math.max(1, Number(cp.quantity ?? 1));
    const unit = round2(Number(cp.Price ?? 0));
    const customLineId = salesLineIdentityKey(cp);
    return {
      ...(isOrderLineObjectId(customLineId) ? { _id: customLineId } : {}),
      product_id: cp.product_id,
      variant_id: cp.variant_id,
      product_name: String(cp.item_name ?? ""),
      sku: `custom_sku_${cp.product_id}`,
      quantity: qty,
      unit_purchase_price: unit,
      unit_retail_price: unit,
      changed_unit_price: unit,
      is_price_overridden: true,
      inventory_source: "custom",
      is_taxable: readResolveTaxable(
        (cp.item_tax_info as Record<string, unknown> | undefined) ?? undefined,
        ["taxable"],
        readResolveTaxable(cp as Record<string, unknown>, ["is_taxable", "taxable"], true)
      ),
      is_production: true,
      custom_information: cp.custom_information,
      attributes: {
        length: String(cp.length ?? ""),
        width: String(cp.width ?? ""),
        height: String(cp.height ?? ""),
        weight: String(cp.weight ?? ""),
      },
    };
  });

  return [...catalogItems, ...customItems];
}
